import React, { useState, useEffect } from "react";
import {
  TrendingDown, // Salidas
  TrendingUp, // Entradas
  RefreshCw, // Cambios
  LayoutDashboard, // Dashboard
  Menu, // Menú
  X, // Cerrar
  LogOut, // Logout
  BarChart3, // Dashboard alternativo
  FileText, // Facturas
  ClipboardList, // Presupuestos
  Target, // Presupuesto Inicial
  Calendar, // Cronograma
  Wallet, // Cajas
  DollarSign, // Cobros
  Scale, // Arqueos
  Building2, // Proyectos
  Settings, // Admin
  Briefcase, // Honorarios y MO
  ChevronLeft, // Flecha para cerrar
  Users, // ✅ NUEVO: KPI Trabajadores
  Crown, // ✅ NUEVO: Para Suscripciones & Pagos
  CreditCard, // ✅ NUEVO: Para Facturación
  UserCog, // ✅ NUEVO: Configuración de cuenta
} from "lucide-react";
import ModalConfiguracionCuenta from "./ModalConfiguracionCuenta";

// ✅ NUEVA IMPORTACIÓN: puedeVerKPITrabajadores y esSuperAdmin
import { puedeVerKPITrabajadores, esSuperAdmin } from "./permisos";

// Colores para cada vista (actualizado con kpi-trabajadores)
const VISTA_COLORS = {
  salidas: "#ef4444",
  entradas: "#10b981",
  cambios: "#6366f1",
  dashboard: "#3b82f6",
  "honorarios-mo": "#f59e0b",
  "facturas-comprobantes": "#84cc16",
  "facturacion": "#0ea5e9",
  presupuestos: "#ec4899",
  "presupuesto-inicial": "#a855f7",
  gantt: "#7c3aed",
  cajas: "#06b6d4",
  cobros: "#f97316",
  arqueos: "#14b8a6",
  proyectos: "#8b5cf6",
  "kpi-trabajadores": "#8b5cf6", // ✅ NUEVO: Color para KPI Trabajadores
  "configuracion-cuenta": "#059669", // ✅ NUEVO: Color para Configuración de Cuenta
  admin: "#64748b",
};

function FloatingSidebar({
  constructora,
  usuario,
  currentView,
  setCurrentView,
  onLogout,
  permisosCliente,
  onUsuarioUpdate, // Para actualizar el estado del usuario
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(currentView);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  // Actualizar activeTab cuando cambie currentView
  useEffect(() => {
    setActiveTab(currentView);
  }, [currentView]);

  // ✅ Control de visibilidad de la barra basado en scroll Y modales
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY || document.documentElement.scrollTop;
      
      // Si está scrolleando hacia abajo y la barra está visible, ocultarla
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false);
      }
      // Si está scrolleando hacia arriba, mostrar la barra
      else if (currentScrollY < lastScrollY || currentScrollY <= 50) {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    // ✅ Verificar si hay un modal abierto (zIndex alto)
    const checkForModal = () => {
      const modalElements = document.querySelectorAll('[style*="z-index: 10000"], [style*="z-index: 99999"], .modal-overlay');
      const hasModalOpen = modalElements.length > 0;
      
      if (hasModalOpen) {
        setIsVisible(false); // Ocultar barra si hay modal
      } else {
        setIsVisible(true); // Mostrar barra si NO hay modal
      }
    };

    // ✅ SIEMPRE agregar el listener en móvil (incluye PWA)
    const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true }); // Fallback para PWA
      
      // ✅ Verificar modales cada 500ms
      const modalCheckInterval = setInterval(checkForModal, 500);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
        clearInterval(modalCheckInterval);
      };
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Verificar acceso a vista
  const checkVistaAccess = (vistaId) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const isConstructoraOrEmpleado =
      usuario?.tipo_usuario === "constructora" ||
      usuario?.tipo_usuario === "empleado";

    // ✅ CONTROL ESPECIAL: Configuración de cuenta (todos los usuarios)
    if (vistaId === "configuracion-cuenta") {
      return true;
    }

    // ✅ CONTROL ESPECIAL: Vistas de sistema (solo super admins)
    if (vistaId.startsWith("system-")) {
      return esSuperAdmin(usuario);
    }

    // ✅ CONTROL ESPECIAL: KPI Trabajadores
    if (vistaId === "kpi-trabajadores") {
      return puedeVerKPITrabajadores(usuario);
    }

    if (isConstructoraOrEmpleado) return true;

    if (isCliente) {
      // Lógica simplificada para clientes
      if (vistaId === "honorarios-mo") return true;
      if (permisosCliente?.permisos?.vistas_permitidas?.includes(vistaId)) {
        return true;
      }
    }
    return false;
  };

  // Vistas principales (siempre visibles en el flotante)
  const mainViews = [
    {
      id: "salidas",
      label: usuario?.tipo_usuario === "cliente" ? "Gastos" : "Salidas",
      icon: TrendingDown,
      color: VISTA_COLORS.salidas,
    },
    {
      id: "entradas",
      label: "Entradas",
      icon: TrendingUp,
      color: VISTA_COLORS.entradas,
    },
    {
      id: "cambios",
      label: "Cambios",
      icon: RefreshCw,
      color: VISTA_COLORS.cambios,
    },
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      color: VISTA_COLORS.dashboard,
    },
  ];

  // Todas las vistas disponibles (actualizado con kpi-trabajadores)
  const allViews = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      color: VISTA_COLORS.dashboard,
    },
    {
      id: "honorarios-mo",
      label: "Honorarios y MO",
      icon: Briefcase,
      color: VISTA_COLORS["honorarios-mo"],
    },
    {
      id: "facturas-comprobantes",
      label: "Facturas",
      icon: FileText,
      color: VISTA_COLORS["facturas-comprobantes"],
    },
    {
      id: "presupuestos",
      label: "Presupuestos",
      icon: ClipboardList,
      color: VISTA_COLORS.presupuestos,
    },
    {
      id: "presupuesto-inicial",
      label: "Presupuesto Inicial",
      icon: Target,
      color: VISTA_COLORS["presupuesto-inicial"],
    },
    ...(usuario?.tipo_usuario === "cliente" ? [] : [{
      id: "gantt",
      label: "Cronograma",
      icon: Calendar,
      color: VISTA_COLORS.gantt,
    }]),
    { id: "cajas", label: "Cajas", icon: Wallet, color: VISTA_COLORS.cajas },
    ...(usuario?.tipo_usuario === "constructora" || usuario?.tipo_usuario === "empleado"
      ? [{ id: "flujos-caja", label: "Flujos de caja", icon: Wallet, color: "#0ea5e9" }]
      : []),
    {
      id: "entradas",
      label: "Entradas",
      icon: TrendingUp,
      color: VISTA_COLORS.entradas,
    },
    {
      id: "salidas",
      label: usuario?.tipo_usuario === "cliente" ? "Gastos" : "Salidas",
      icon: TrendingDown,
      color: VISTA_COLORS.salidas,
    },
    {
      id: "cambios",
      label: "Cambios",
      icon: RefreshCw,
      color: VISTA_COLORS.cambios,
    },
    {
      id: "cobros",
      label: "Cobros",
      icon: DollarSign,
      color: VISTA_COLORS.cobros,
    },
    {
      id: "arqueos",
      label: "Arqueos",
      icon: Scale,
      color: VISTA_COLORS.arqueos,
    },
    {
      id: "proyectos",
      label: "Proyectos",
      icon: Building2,
      color: VISTA_COLORS.proyectos,
    },
    // ✅ NUEVA VISTA: KPI Trabajadores
    {
      id: "kpi-trabajadores",
      label: "KPI Trabajadores",
      icon: Users,
      color: VISTA_COLORS["kpi-trabajadores"],
    },
    { id: "admin", label: "Admin", icon: Settings, color: VISTA_COLORS.admin },
    {
      id: "facturacion",
      label: "Facturación",
      icon: CreditCard,
      color: "#0ea5e9",
    },
    // ✅ NUEVA VISTA: Suscripciones & Pagos (solo super admin)
    {
      id: "system-admin",
      label: "Suscripciones & Pagos",
      icon: Crown,
      color: "#dc2626",
    },
  ].filter((view) => checkVistaAccess(view.id));

  // En mainViews y allViews, si es cliente y es llave en mano, solo retornar el ítem honorarios-mo con label 'Resumen de obra'.
  const filteredMainViews = mainViews.filter((view) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const esLlaveEnMano = permisosCliente?.tipoProyecto === 'llave_en_mano';
    return isCliente && esLlaveEnMano ? view.id === "honorarios-mo" : true;
  });

  const filteredAllViews = allViews.filter((view) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const esLlaveEnMano = permisosCliente?.tipoProyecto === 'llave_en_mano';
    return isCliente && esLlaveEnMano ? view.id === "honorarios-mo" : true;
  });

  const handleTabClick = (viewId) => {
    setCurrentView(viewId);
    setActiveTab(viewId);
  };

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    onLogout();
  };

  // 1. Crear una función handleDrawerScroll que permita el scroll solo si hay contenido para scrollear, y si no, previene el scroll del fondo.
  const handleDrawerScroll = (e) => {
    const drawer = e.currentTarget;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;

    if (isScrollingDown) {
      if (drawer.scrollTop + drawer.clientHeight >= drawer.scrollHeight - 10) {
        e.preventDefault();
      }
    } else if (isScrollingUp) {
      if (drawer.scrollTop <= 10) {
        e.preventDefault();
      }
    }
  };

  return (
    <>
      {/* Sidebar flotante principal */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
          borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          padding: "8px 16px 16px 16px",
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60px",
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
          transform: isVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {filteredMainViews.map((view) => {
          const IconComponent = view.icon;
          const isActive = activeTab === view.id;

          return (
            <button
              key={view.id}
              onClick={() => handleTabClick(view.id)}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                padding: "8px 6px",
                background: isActive ? "rgba(59, 130, 246, 0.08)" : "transparent",
                border: "none",
                borderRadius: "16px",
                color: isActive ? "#3b82f6" : "#64748b",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                minHeight: "48px",
                justifyContent: "center",
                outline: 'none',
                boxShadow: 'none',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.target.style.background = "rgba(100, 116, 139, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.target.style.background = "transparent";
                }
              }}
              onFocus={e => e.target.blur()}
            >
              <IconComponent size={20} />
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  userSelect: 'none',
                  boxShadow: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
                tabIndex={-1}
              >
                {view.label}
              </span>
            </button>
          );
        })}

        {/* Botón de menú */}
        <button
          onClick={handleMenuClick}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            padding: "8px 6px",
            background: isMenuOpen ? "#64748b" : "transparent",
            border: "none",
            borderRadius: "16px",
            color: isMenuOpen ? "white" : "#64748b",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            minHeight: "48px",
            justifyContent: "center",
            minWidth: "50px",
            outline: 'none',
            boxShadow: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!isMenuOpen) {
              e.target.style.background = "rgba(100, 116, 139, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMenuOpen) {
              e.target.style.background = "transparent";
            }
          }}
          onFocus={e => e.target.blur()}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          <span
            style={{
              fontSize: "10px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              userSelect: 'none',
              boxShadow: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
            tabIndex={-1}
          >
            Más
          </span>
        </button>
      </div>


      {/* Menú lateral */}
      {isMenuOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            animation: "fadeIn 0.3s ease-out",
          }}
          onClick={() => setIsMenuOpen(false)}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: "280px",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRight: "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              overflowY: "auto",
              overscrollBehavior: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={handleDrawerScroll}
            onTouchMove={handleDrawerScroll}
          >
            {/* Header del menú */}
            <div
              style={{
                padding: "24px 20px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Building2 size={20} color="white" />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  {constructora?.nombre || "Mi Empresa"}
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    {usuario?.nombre} {usuario?.apellido}
                  </p>
                  <button
                    onClick={() => setIsConfigModalOpen(true)}
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                      background: "transparent",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#64748b",
                      transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#e2e8f0";
                      e.target.style.color = "#3b82f6";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.color = "#64748b";
                    }}
                  >
                    <Settings size={10} />
                  </button>
                </div>
              </div>
            </div>

            {/* Lista de vistas */}
            <div style={{ padding: "16px 0" }}>
              <p
                style={{
                  margin: "0 20px 12px 20px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Navegación
              </p>

              {filteredAllViews.map((view) => {
                const IconComponent = view.icon;
                const isActive = activeTab === view.id;

                return (
                  <button
                    key={view.id}
                    onClick={() => {
                      handleTabClick(view.id);
                      setIsMenuOpen(false);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 20px",
                      background: isActive ? "rgba(59, 130, 246, 0.08)" : "transparent",
                      border: "none",
                      color: isActive ? "#3b82f6" : "#334155",
                      cursor: "pointer",
                      transition: "all 0.2s ease-out",
                      textAlign: "left",
                      outline: 'none',
                      boxShadow: 'none',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.target.style.background = "rgba(100, 116, 139, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.target.style.background = "transparent";
                      }
                    }}
                    onFocus={e => e.target.blur()}
                  >
                    <IconComponent size={18} />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: isActive ? "600" : "500",
                        userSelect: 'none',
                        boxShadow: 'none',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                      tabIndex={-1}
                    >
                      {view.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Botón de logout */}
            <div
              style={{
                padding: "16px 20px",
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <button
                onClick={handleLogout}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 16px",
                  background: "rgba(239, 68, 68, 0.05)",
                  border: "1px solid rgba(239, 68, 68, 0.1)",
                  borderRadius: "12px",
                  color: "#ef4444",
                  cursor: "pointer",
                  transition: "all 0.2s ease-out",
                  fontSize: "14px",
                  fontWeight: "500",
                  outline: 'none',
                  boxShadow: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(239, 68, 68, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(239, 68, 68, 0.05)";
                }}
                onFocus={e => e.target.blur()}
              >
                <LogOut size={18} />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        body {
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        button:focus { outline: none !important; box-shadow: none !important; }
        *:focus, *:active { outline: none !important; box-shadow: none !important; }
        ::selection { background: transparent !important; }
      `}</style>

      {/* Modal de Configuración de Cuenta */}
      <ModalConfiguracionCuenta
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        usuario={usuario}
        constructora={constructora}
        onUsuarioUpdate={onUsuarioUpdate}
      />
    </>
  );
}

export default FloatingSidebar;
